import {CopilotDiffChatContextMenu} from '@github-ui/copilot-code-chat/CopilotDiffChatContextMenu'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import type {FileDiffReference} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {
  CommentDiscussionIcon,
  CopyIcon,
  FoldDownIcon,
  FoldUpIcon,
  LinkIcon,
  MultiSelectIcon,
} from '@primer/octicons-react'
import {ActionList} from '@primer/react'
import {useCallback, type MouseEvent} from 'react'

import useExpandHunk from '../../hooks/use-expand-hunk'
import type {ClientDiffLine, DiffLine, DiffSide} from '../../types'
import {StartConversationContextMenuItems} from '../StartConversationContextMenuItems'
import {KeybindingHint} from '@primer/react/experimental'
import {CommentsPreference} from '@github-ui/diff-view-settings/types'
import {copyText} from '@github-ui/copy-to-clipboard'
import {anchorLinkForSelection, copilotSelectedDiffLineRange} from '../../helpers/line-helpers'
import {DiffFeature, type DiffAnchor, type SimpleDiffLine} from '@github-ui/diffs/types'
import {useGetSelectedLineRange, useHiddenFeatures} from '../../stores/selectors'
import type {HunkData} from '../../helpers/hunk-data-helpers'
import type {DiffAnnotation, Thread, ViewerData} from '../../conversation-types'

type CellContextMenuProps = {
  currentHunk: HunkData | undefined
  diffAnchor: DiffAnchor
  diffLine: ClientDiffLine
  showStartConversation: boolean
  shouldDisplayCollapseComments?: boolean
  copilotChatReference?: FileDiffReference
  handleViewMarkersSelection: (markerLoc?: DiffSide) => void
  isLeftSide: boolean
  isSplit: boolean
  startConversationCurrentLine: () => void
  startConversationWithSuggestedChange: () => void
  handleCopyCode?: () => void
  nextHunk: HunkData | undefined
  previousHunk: HunkData | undefined
  threads: Thread[]
  annotations: DiffAnnotation[]
  viewerData: ViewerData
}

export function CellContextMenu(props: CellContextMenuProps) {
  return (
    <>
      <MarkerListItems {...props} />
      {props.viewerData.shouldDisplayComments && (
        <CopilotListItems
          diffAnchor={props.diffAnchor}
          diffLine={props.diffLine}
          fileDiffReference={props.copilotChatReference}
          isLeftSide={props.isLeftSide}
        />
      )}
      <CopyContentListItems handleCopyCode={props.handleCopyCode} />
      <SelectAllListItem diffAnchor={props.diffAnchor} />
      {props.viewerData.shouldDisplayComments && (
        <CopyAnchorLink diffAnchor={props.diffAnchor} diffLine={props.diffLine} />
      )}
      <HunkListItems currentHunk={props.currentHunk} nextHunk={props.nextHunk} previousHunk={props.previousHunk} />
    </>
  )
}

function CopyAnchorLink({diffLine, diffAnchor}: {diffAnchor: DiffAnchor; diffLine: ClientDiffLine}) {
  const getSelectedLineRange = useGetSelectedLineRange()
  const line = diffLine as SimpleDiffLine

  const copyAnchorLink = useCallback(() => {
    const lineRange = getSelectedLineRange()
    const link = anchorLinkForSelection({line, range: lineRange, fileAnchor: diffAnchor})
    if (link) copyText(link)
  }, [diffAnchor, getSelectedLineRange, line])

  return (
    <ActionList.Item
      onSelect={copyAnchorLink}
      onMouseDown={(event: MouseEvent) => {
        // We need to prevent the window's Selection object from being reset on mouse down if user has selected text.
        // This is to prevent the browser from resetting the selection before onSelect event callback is called.
        if (window.getSelection()?.toString() !== '') event.preventDefault()
      }}
    >
      <ActionList.LeadingVisual>
        <LinkIcon />
      </ActionList.LeadingVisual>
      Copy link
      <ActionList.TrailingVisual>
        <KeybindingHint keys="Mod+Alt+y" />
      </ActionList.TrailingVisual>
    </ActionList.Item>
  )
}

const CopilotListItems: React.FC<{
  diffAnchor: DiffAnchor
  diffLine: ClientDiffLine
  fileDiffReference?: FileDiffReference
  isLeftSide: boolean
}> = props => {
  const getSelectedLineRange = useGetSelectedLineRange()
  const selectedLineRange = getSelectedLineRange()

  const copilotSelectedDiffRowRange = copilotSelectedDiffLineRange(
    selectedLineRange,
    props.diffLine,
    props.isLeftSide,
    props.diffAnchor,
  )

  if (!props.fileDiffReference) return null

  return (
    <CopilotDiffChatContextMenu
      showDivider
      selectedRange={copilotSelectedDiffRowRange}
      fileDiffReference={props.fileDiffReference}
    />
  )
}

function SelectAllListItem({diffAnchor}: {diffAnchor: DiffAnchor}) {
  const handleSelectAllSelection = () => {
    // We need to wait one click for the context menu to close before we allow for the event to occur inside of the table instead of context menu
    setTimeout(() => {
      document
        .querySelector(`table[data-diff-anchor="${diffAnchor}"]`)
        ?.dispatchEvent(new KeyboardEvent('keydown', {key: 'a', code: 'KeyA', ctrlKey: true}))
    })
  }

  return (
    <ActionList.Item onSelect={handleSelectAllSelection}>
      <ActionList.LeadingVisual>
        <MultiSelectIcon />
      </ActionList.LeadingVisual>
      Select all
      <ActionList.TrailingVisual>
        <KeybindingHint keys="Mod+a" />
      </ActionList.TrailingVisual>
    </ActionList.Item>
  )
}

function MarkerListItems({
  diffLine,
  handleViewMarkersSelection,
  isLeftSide,
  isSplit,
  shouldDisplayCollapseComments,
  showStartConversation,
  startConversationCurrentLine,
  startConversationWithSuggestedChange,
  threads,
  annotations,
  viewerData,
}: Omit<CellContextMenuProps, 'copilotChatReference'>) {
  const hasAnnotations = annotations.length > 0

  if (!showStartConversation && !threads && !hasAnnotations) {
    return null
  }

  return (
    <>
      {viewerData.shouldDisplayComments && showStartConversation && (
        <StartConversationContextMenuItems
          diffLine={diffLine}
          handleStartConversation={startConversationCurrentLine}
          handleStartConversationWithSuggestedChange={startConversationWithSuggestedChange}
          isLeftSide={isLeftSide}
          isSplit={isSplit}
        />
      )}
      <ViewMarkerListItems
        threads={threads}
        annotations={annotations}
        diffLine={diffLine}
        handleViewMarkersSelection={handleViewMarkersSelection}
        isLeftSide={isLeftSide}
        isSplit={isSplit}
        shouldDisplayCollapseComments={shouldDisplayCollapseComments}
        viewerData={viewerData}
      />
      <ActionList.Divider />
    </>
  )
}

function ViewMarkerListItems({
  diffLine,
  handleViewMarkersSelection,
  isLeftSide,
  isSplit,
  threads,
  annotations,
  shouldDisplayCollapseComments,
  viewerData,
}: {
  diffLine: ClientDiffLine
  handleViewMarkersSelection: (markerLoc?: DiffSide) => void
  isLeftSide: boolean
  isSplit: boolean
  threads: Thread[]
  annotations: DiffAnnotation[]
  shouldDisplayCollapseComments?: boolean
  viewerData: ViewerData
}) {
  if (viewerData.commentsPreference === CommentsPreference.Visible) {
    return null
  }

  if (!isSplit) {
    return (
      <UnifiedDiffMarkerListItem
        threads={threads}
        annotations={annotations}
        diffLine={diffLine}
        onSelect={() => handleViewMarkersSelection()}
        shouldDisplayCollapseComments={shouldDisplayCollapseComments}
      />
    )
  }

  return (
    <SplitDiffMarkersListItems
      threads={threads}
      annotations={annotations}
      handleViewMarkersSelection={handleViewMarkersSelection}
      isLeftSide={isLeftSide}
      shouldDisplayCollapseComments={shouldDisplayCollapseComments}
    />
  )
}

function SplitDiffMarkersListItems({
  handleViewMarkersSelection,
  isLeftSide,
  shouldDisplayCollapseComments,
  threads,
  annotations,
}: {
  handleViewMarkersSelection: (markerLoc: DiffSide) => void
  isLeftSide: boolean
  shouldDisplayCollapseComments?: boolean
  threads: Thread[]
  annotations: DiffAnnotation[]
}) {
  const hasThreads = threads.length > 0
  const hasAnnotations = annotations.length > 0
  if (isLeftSide) {
    return (
      <>
        {hasThreads && (
          <MarkerListItem
            text={shouldDisplayCollapseComments ? 'Collapse comments' : 'Expand comments'}
            onSelect={() => handleViewMarkersSelection('LEFT')}
          />
        )}
      </>
    )
  }

  return (
    <>
      {(hasThreads || hasAnnotations) && (
        <MarkerListItem
          text={shouldDisplayCollapseComments ? 'Collapse comments' : 'Expand comments'}
          onSelect={() => handleViewMarkersSelection('RIGHT')}
        />
      )}
    </>
  )
}

function UnifiedDiffMarkerListItem({
  diffLine,
  onSelect,
  threads,
  annotations,
  shouldDisplayCollapseComments,
}: {
  diffLine: ClientDiffLine
  onSelect: () => void
  threads: Thread[]
  annotations: DiffAnnotation[]
  shouldDisplayCollapseComments?: boolean
}) {
  const line = diffLine as DiffLine
  const hasMarkers = line && (threads.length > 0 || annotations.length > 0)
  if (!hasMarkers) return null
  return (
    <MarkerListItem
      text={shouldDisplayCollapseComments ? 'Collapse comments' : 'Expand comments'}
      onSelect={() => onSelect()}
    />
  )
}

function MarkerListItem({onSelect, text}: {onSelect: () => void; text: string}) {
  return (
    <ActionList.Item onSelect={onSelect}>
      <ActionList.LeadingVisual>
        <CommentDiscussionIcon />
      </ActionList.LeadingVisual>
      {text}
    </ActionList.Item>
  )
}

function CopyContentListItems({handleCopyCode}: {handleCopyCode?: () => void}) {
  return (
    <ActionList.Item
      onSelect={handleCopyCode}
      onMouseDown={(event: MouseEvent) => {
        // We need to prevent the window's Selection object from being reset on mouse down if user has selected text.
        // This is to prevent the browser from resetting the selection before onSelect event callback is called.
        if (window.getSelection()?.toString() !== '') event.preventDefault()
      }}
    >
      <ActionList.LeadingVisual>
        <CopyIcon />
      </ActionList.LeadingVisual>
      Copy
      <ActionList.TrailingVisual>
        <KeybindingHint keys="Mod+c" />
      </ActionList.TrailingVisual>
    </ActionList.Item>
  )
}

function HunkListItems({
  currentHunk,
  nextHunk,
  previousHunk,
}: {
  currentHunk: HunkData | undefined
  nextHunk: HunkData | undefined
  previousHunk: HunkData | undefined
}) {
  const hiddenFeatures = useHiddenFeatures()

  const {canExpandStartOfHunk, canExpandEndOfHunk} = useExpandHunk({currentHunk, nextHunk, previousHunk})

  if (!canExpandStartOfHunk && !canExpandEndOfHunk && !previousHunk && !nextHunk) return null

  return (
    <>
      <ActionList.Divider />
      {/** If exanding diff lines is a hidden feature, we don't render expanding hunk list items */}
      {!hiddenFeatures.has(DiffFeature.expandLines) ? (
        <ExpandHunksListItems currentHunk={currentHunk} nextHunk={nextHunk} previousHunk={previousHunk} />
      ) : null}
    </>
  )
}

function ExpandHunksListItems({
  currentHunk,
  nextHunk,
  previousHunk,
}: {
  currentHunk: HunkData | undefined
  nextHunk: HunkData | undefined
  previousHunk: HunkData | undefined
}) {
  const {canExpandStartOfHunk, expandEndOfHunk, expandStartOfHunk, canExpandEndOfHunk} = useExpandHunk({
    currentHunk,
    nextHunk,
    previousHunk,
  })

  return (
    <>
      {canExpandStartOfHunk && (
        <ActionList.Item onSelect={expandStartOfHunk}>
          <ActionList.LeadingVisual>
            <FoldUpIcon />
          </ActionList.LeadingVisual>
          Expand above
        </ActionList.Item>
      )}
      {canExpandEndOfHunk && (
        <ActionList.Item onSelect={expandEndOfHunk}>
          <ActionList.LeadingVisual>
            <FoldDownIcon />
          </ActionList.LeadingVisual>
          Expand below
        </ActionList.Item>
      )}
    </>
  )
}

try{ CellContextMenu.displayName ||= 'CellContextMenu' } catch {}
try{ CopyAnchorLink.displayName ||= 'CopyAnchorLink' } catch {}
try{ SelectAllListItem.displayName ||= 'SelectAllListItem' } catch {}
try{ MarkerListItems.displayName ||= 'MarkerListItems' } catch {}
try{ ViewMarkerListItems.displayName ||= 'ViewMarkerListItems' } catch {}
try{ SplitDiffMarkersListItems.displayName ||= 'SplitDiffMarkersListItems' } catch {}
try{ UnifiedDiffMarkerListItem.displayName ||= 'UnifiedDiffMarkerListItem' } catch {}
try{ MarkerListItem.displayName ||= 'MarkerListItem' } catch {}
try{ CopyContentListItems.displayName ||= 'CopyContentListItems' } catch {}
try{ HunkListItems.displayName ||= 'HunkListItems' } catch {}
try{ ExpandHunksListItems.displayName ||= 'ExpandHunksListItems' } catch {}